import { useState } from 'react';
import type { JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { EmptyState, ErrorState, Field, LoadingState, Panel } from '../components/ui.js';

/**
 * Galerie náhledů sady šablon (D-313): všechny šablony sady vedle sebe, světlý nebo tmavý vzhled,
 * šířka telefonu nebo počítače. Náhled renderuje server stejnou cestou jako odeslání.
 */
interface SetInfo {
  id: string;
  name: string;
  count: number;
}
interface Gallery {
  id: string;
  name: string;
  items: Array<{ base: string; name: string; subject: string; html: string }>;
}

export function TemplateGallery(): JSX.Element {
  const sets = useQuery({
    queryKey: ['race-sets'],
    queryFn: () => api.get<{ items: SetInfo[] }>('/api/templates/race-sets'),
  });
  const [setId, setSetId] = useState('monza');
  const [scheme, setScheme] = useState<'light' | 'dark'>('light');
  const [width, setWidth] = useState<'mobile' | 'desktop'>('mobile');
  const gallery = useQuery({
    queryKey: ['race-set-gallery', setId, scheme],
    queryFn: () =>
      api.post<Gallery>(`/api/templates/race-sets/${encodeURIComponent(setId)}/gallery`, {
        brand: 'TGP',
        scheme,
      }),
  });
  const px = width === 'mobile' ? 375 : 640;

  return (
    <Panel title="Galerie sad šablon">
      <p className="note">
        Všechny šablony sady vedle sebe, tak jak je uvidí příjemce. Obsah sady vychází z veřejného
        webu; publikaci a nahrání do SmartEmailingu děláte zvlášť (Editor šablon, Mapování).
      </p>
      <div className="row">
        <Field label="Sada">
          <select value={setId} onChange={(e) => setSetId(e.target.value)}>
            {(sets.data?.items ?? [{ id: 'monza', name: 'F1 Monza 2027', count: 8 }]).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.count})
              </option>
            ))}
          </select>
        </Field>
        <Field label="Vzhled">
          <select value={scheme} onChange={(e) => setScheme(e.target.value as 'light' | 'dark')}>
            <option value="light">světlý</option>
            <option value="dark">tmavý</option>
          </select>
        </Field>
        <Field label="Šířka">
          <select value={width} onChange={(e) => setWidth(e.target.value as 'mobile' | 'desktop')}>
            <option value="mobile">telefon (375 px)</option>
            <option value="desktop">počítač (640 px)</option>
          </select>
        </Field>
      </div>
      {gallery.isLoading ? <LoadingState what="náhledy" /> : null}
      {gallery.isError ? (
        <ErrorState
          title="Náhledy se nepodařilo vykreslit"
          error={gallery.error}
          onRetry={() => void gallery.refetch()}
        />
      ) : null}
      {gallery.data && gallery.data.items.length === 0 ? (
        <EmptyState title="Sada je prázdná">Žádné šablony k zobrazení.</EmptyState>
      ) : null}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
        {gallery.data?.items.map((item) => (
          <figure key={item.base} style={{ margin: 0, width: px }}>
            <figcaption style={{ fontWeight: 700, padding: '4px 0' }}>{item.name}</figcaption>
            <div className="note" style={{ paddingBottom: 6 }}>
              Předmět: {item.subject}
            </div>
            <iframe
              title={`Náhled: ${item.name}`}
              srcDoc={item.html}
              sandbox=""
              style={{
                width: px,
                height: 760,
                border: '1px solid var(--border, #ddd)',
                borderRadius: 8,
                background: scheme === 'dark' ? '#0e0e0f' : '#f8f8f8',
              }}
            />
          </figure>
        ))}
      </div>
    </Panel>
  );
}
